import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useTranslation } from '../../hooks/useTranslation';
import { formatDateWithTimezone } from '../../utils/dateUtils';
import Navbar from '../Navbar';

export default function Statistics() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [decks, setDecks] = useState([]);
  const [deckStats, setDeckStats] = useState({});
  const [sessions, setSessions] = useState([]);
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [loadError, setLoadError] = useState('');
  const [timezone, setTimezone] = useState('UTC');

  const loadStats = () => api.get('/statistics').then(({ data }) => setStats(data)).catch(() => setLoadError(t('stats.loadError')));
  const loadSessions = () => api.get('/sessions').then(({ data }) => setSessions(data)).catch(() => {});

  useEffect(() => {
    loadStats();
    api.get('/decks').then(({ data }) => setDecks(data)).catch(() => {});
    loadSessions();
    api.get('/admin/public-settings')
      .then(({ data }) => { if (data.timezone) setTimezone(data.timezone); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDeckStats = async (deckId) => {
    if (deckStats[deckId]) return;
    const { data } = await api.get(`/statistics/decks/${deckId}`);
    setDeckStats((prev) => ({ ...prev, [deckId]: data }));
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm(t('stats.deleteSessionConfirm'))) return;
    try {
      await api.delete(`/sessions/${id}`);
      loadSessions();
    } catch {
      alert(t('stats.failedDelete'));
    }
  };

  const handleDeleteAllSessions = async () => {
    if (!window.confirm(t('stats.deleteAllConfirm'))) return;
    try {
      await api.delete('/sessions');
      loadSessions();
    } catch {
      alert(t('stats.failedDeleteAll'));
    }
  };

  const handleClearStatistics = async () => {
    if (!window.confirm(t('stats.clearConfirm'))) return;
    try {
      await api.delete('/statistics');
      setDeckStats({});
      loadStats();
    } catch {
      alert(t('stats.failedClear'));
    }
  };

  const toggleSessionExpand = (id) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const formatDate = (iso) => formatDateWithTimezone(iso, timezone);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">{t('stats.title')}</h2>
          <button
            onClick={handleClearStatistics}
            className="text-sm text-red-500 hover:text-red-700 border border-red-300 dark:border-red-800 rounded-lg px-3 py-1.5 hover:bg-red-50 dark:hover:bg-red-950 transition"
          >
            {t('stats.clearStatistics')}
          </button>
        </div>

        {loadError && <p className="text-red-500 text-sm mb-4">{loadError}</p>}

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {[
              { label: t('stats.totalReviews'), value: stats.total_reviews },
              { label: t('stats.correct'), value: stats.correct },
              { label: t('stats.wrong'), value: stats.wrong },
              { label: t('stats.accuracy'), value: `${stats.accuracy}%` },
              { label: t('stats.totalSessions'), value: stats.total_sessions },
              { label: t('stats.completedSessions'), value: stats.completed_sessions },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white dark:bg-gray-900 rounded-xl shadow p-4 text-center">
                <p className="text-2xl font-bold text-indigo-600">{value}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{label}</p>
              </div>
            ))}
          </div>
        )}

        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4">{t('stats.byDeck')}</h3>
        {decks.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500">{t('stats.noDecks')}</p>
        ) : (
          <div className="space-y-3 mb-8">
            {decks.map((deck) => {
              const ds = deckStats[deck.id];
              return (
                <div
                  key={deck.id}
                  className="bg-white dark:bg-gray-900 rounded-xl shadow p-4 cursor-pointer hover:shadow-md transition"
                  onClick={() => loadDeckStats(deck.id)}
                >
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-100">{deck.name}</h4>
                    {!ds && (
                      <span className="text-xs text-indigo-500">{t('stats.clickToLoad')}</span>
                    )}
                  </div>
                  {ds && (
                    <>
                      <div className="grid grid-cols-4 gap-2 mt-3 text-center text-sm">
                        <div>
                          <p className="font-bold text-indigo-600">{ds.total_reviews}</p>
                          <p className="text-gray-500 dark:text-gray-400">{t('stats.reviews')}</p>
                        </div>
                        <div>
                          <p className="font-bold text-green-600">{ds.correct}</p>
                          <p className="text-gray-500 dark:text-gray-400">{t('stats.correct')}</p>
                        </div>
                        <div>
                          <p className="font-bold text-red-500">{ds.wrong}</p>
                          <p className="text-gray-500 dark:text-gray-400">{t('stats.wrong')}</p>
                        </div>
                        <div>
                          <p className="font-bold text-indigo-600">{ds.accuracy}%</p>
                          <p className="text-gray-500 dark:text-gray-400">{t('stats.accuracy')}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-center text-sm">
                        <div>
                          <p className="font-bold text-indigo-500">{ds.total_sessions}</p>
                          <p className="text-gray-500 dark:text-gray-400">{t('stats.totalSessions')}</p>
                        </div>
                        <div>
                          <p className="font-bold text-green-500">{ds.completed_sessions}</p>
                          <p className="text-gray-500 dark:text-gray-400">{t('stats.completedSessions')}</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">{t('stats.sessions')}</h3>
          {sessions.length > 0 && (
            <button
              onClick={handleDeleteAllSessions}
              className="text-sm text-red-500 hover:text-red-700 border border-red-300 dark:border-red-800 rounded-lg px-3 py-1.5 hover:bg-red-50 dark:hover:bg-red-950 transition"
            >
              {t('stats.deleteAllSessions')}
            </button>
          )}
        </div>
        {sessions.length === 0 ? (
          <p className="text-gray-400 dark:text-gray-500">{t('stats.noSessions')}</p>
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => {
              const total = s.correct_count + s.wrong_count;
              const accuracy = total ? Math.round((s.correct_count / total) * 100) : 0;
              const sessionTypeLabel = t(`stats.sessionTypes.${s.session_type}`);
              const sessionTypeBadgeColors = {
                trivia: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300',
                fill: 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300',
                study: 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300',
                questionnaire: 'bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300',
              };
              const sessionTypeBadgeColor = sessionTypeBadgeColors[s.session_type] || sessionTypeBadgeColors.study;
              const isExpanded = expandedSessions.has(s.id);
              return (
                <div key={s.id} className="bg-white dark:bg-gray-900 rounded-xl shadow p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-800 dark:text-gray-100">{s.deck_name}</p>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${sessionTypeBadgeColor}`}>
                          {sessionTypeLabel}
                        </span>
                        {s.is_completed ? (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                            {t('stats.completed')}
                          </span>
                        ) : (
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                            {t('stats.incomplete')}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{formatDate(s.started_at)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-indigo-600">{accuracy}%</span>
                      {s.card_history && s.card_history.length > 0 && (
                        <button
                          onClick={() => toggleSessionExpand(s.id)}
                          className="text-xs text-indigo-500 hover:text-indigo-700"
                          title={isExpanded ? t('stats.hideHistory') : t('stats.showHistory')}
                        >
                          {isExpanded ? '▲' : '▼'}
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteSession(s.id)}
                        className="text-xs text-red-400 hover:text-red-600"
                        title="Delete session"
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-4 mt-3 text-sm">
                    <span className="text-green-600 font-medium">{t('stats.correctCount', { n: s.correct_count })}</span>
                    <span className="text-red-500 font-medium">{t('stats.wrongCount', { n: s.wrong_count })}</span>
                    {s.total_cards_in_session > 0 ? (
                      <span className="text-gray-400 dark:text-gray-500">{t('stats.cardsAddedReviewed', { reviewed: s.cards_reviewed, total: s.total_cards_in_session })}</span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">{t('stats.total', { n: total })}</span>
                    )}
                  </div>
                  {isExpanded && s.card_history && s.card_history.length > 0 && (
                    <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">{t('stats.cardHistory')}</p>
                      <div className="flex flex-col gap-1.5 max-h-64 overflow-y-auto pr-1">
                        {s.card_history.map((entry, idx) => (
                          entry.question ? (
                            <div
                              key={idx}
                              className={`text-xs rounded px-2 py-1.5 ${
                                entry.correct
                                  ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800'
                                  : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
                              }`}
                            >
                              <div className="flex items-start gap-1.5">
                                <span className={`font-bold shrink-0 ${entry.correct ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                                  {entry.correct ? '✓' : '✗'}
                                </span>
                                <div className="min-w-0">
                                  <p className="text-gray-700 dark:text-gray-200 break-words">
                                    <span className="font-semibold text-gray-400 dark:text-gray-500">{t('stats.question')}: </span>{entry.question}
                                  </p>
                                  <p className="text-gray-500 dark:text-gray-400 break-words">
                                    <span className="font-semibold text-gray-400 dark:text-gray-500">{t('stats.answer')}: </span>{entry.answer}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <span
                              key={idx}
                              className={`text-xs px-1.5 py-0.5 rounded font-mono self-start ${
                                entry.correct
                                  ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                  : 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
                              }`}
                              title={`Card #${entry.card_id}: ${entry.correct ? t('stats.correct') : t('stats.wrong')}`}
                            >
                              {entry.correct ? '✓' : '✗'}
                            </span>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
